/**
 * @fileoverview AR-670 — student "Add from Google Classroom" picker: lists the
 * files the student THEMSELVES posted on the linked course stream (GET
 * google_classroom_files — announcements; the backend resolves the course),
 * pre-selects everything (prototype parity) and attaches the picked ones like
 * the automatic import does: `drive_file` becomes a mirrored Drive record,
 * every other kind (link/youtube/form) goes through the delivery-links flow.
 * Already-attached URLs are filtered out (dedup). Unavailable states are 200
 * {available:false, reason} — rendered as an informative alert, never errors.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Anchor, Button, Checkbox, Group, Loader, Modal, Stack, Text } from '@mantine/core'
import { IconExternalLink, IconInfoCircle } from '@tabler/icons-react'

import { notifications } from 'context'
import googleDriveUploadService from '@legacy/common/services/googleDriveUploadService'
import { getClassroomAdapters, StudentClassroomFiles } from 'integrations/classroom'

/** available:false reasons → copy (contract: never an error state). */
const REASON_MESSAGE_KEYS: Record<string, string> = {
  project_not_linked: 'projectClassroomNotLinked',
  student_not_mapped: 'classroomSyncMissingStudentLink',
  school_not_connected: 'projectClassroomNeedsConnection',
  // School connected before the announcements scope existed — reconnect.
  missing_announcements_scope: 'classroomNeedsAnnouncementsScope',
}

interface StudentClassroomPickerProps {
  readonly opened: boolean
  readonly onClose: () => void
  /** Student task identifier (GET /api/student_tasks/:id/google_classroom_files). */
  readonly studentTaskId: string
  /** Delivery receiving the mirrored attachments. */
  readonly studentTaskDeliveryId: string
  /** URLs already attached to the delivery (drive mirrors + links) — hidden here. */
  readonly existingUrls: readonly string[]
  /** Non-drive attachments reuse the delivery-links flow (same as "Add link"). */
  readonly onAttachLink: (url: string) => Promise<'success' | 'invalid' | 'error'>
  /** Called after at least one attachment was added (reload + auto-submit). */
  readonly onAdded: () => void
}

export function StudentClassroomPicker({
  opened,
  onClose,
  studentTaskId,
  studentTaskDeliveryId,
  existingUrls,
  onAttachLink,
  onAdded,
}: StudentClassroomPickerProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [data, setData] = useState<StudentClassroomFiles | null>(null)
  // SNAPSHOT of the offerable files, frozen at load: existingUrls mutates
  // while the modal is open (each attached link reloads the parent), and a
  // per-render re-filter would shift the index-based selection onto other
  // files. Dedup happens once, here. Index identity — stream files have no id
  // and URLs can repeat.
  const [attachments, setAttachments] = useState<StudentClassroomFiles['files']>([])
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const tr = (key: string) => t(`classroom.${key}`)

  useEffect(() => {
    if (!opened) return undefined
    let cancelled = false
    setLoading(true)
    setFailed(false)
    setData(null)
    setAttachments([])
    setSelected(new Set())
    getClassroomAdapters()
      .submissionSync.getStudentFiles(studentTaskId)
      .then(result => {
        if (cancelled) return
        setData(result)
        const available = (result?.files ?? []).filter(
          attachment => !existingUrls.includes(attachment.url),
        )
        setAttachments(available)
        // Prototype parity: everything pre-selected — the student only confirms.
        setSelected(new Set(available.map((_, index) => index)))
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // existingUrls left out on purpose: the list must reload only on open.
  }, [opened, studentTaskId])

  const allSelected = attachments.length > 0 && selected.size === attachments.length

  const toggle = (index: number) => {
    const next = new Set(selected)
    next.has(index) ? next.delete(index) : next.add(index)
    setSelected(next)
  }

  const handleAttach = async () => {
    const pickedIndexes = attachments.map((_, index) => index).filter(index => selected.has(index))
    if (pickedIndexes.length === 0) return
    setSubmitting(true)
    const addedIndexes = new Set<number>()
    let failures = 0
    // Sequential on purpose: Rails nested-attributes writes on the same
    // delivery, and a couple of files do not justify a parallel burst.
    for (const index of pickedIndexes) {
      const attachment = attachments[index]
      try {
        if (attachment.kind === 'drive_file') {
          await googleDriveUploadService.activity.postFile({
            resourceClass: 'student_task_delivery_id',
            resourceId: studentTaskDeliveryId,
            file: {
              name: attachment.name,
              description: '',
              url: attachment.url,
              iconUrl: attachment.iconUrl || '',
              mimeType: '',
            },
          })
          addedIndexes.add(index)
        } else {
          const outcome = await onAttachLink(attachment.url)
          outcome === 'success' ? addedIndexes.add(index) : failures++
        }
      } catch {
        failures++
      }
    }
    setSubmitting(false)
    if (addedIndexes.size > 0) {
      notifications.show({ color: 'green', message: tr('classroomFilesAddedSuccessfully') })
      onAdded()
    }
    if (failures > 0) {
      // Keep the modal open on the REMAINING files only: drop what was
      // attached from the snapshot and reset the selection to the failures.
      setAttachments(attachments.filter((_, index) => !addedIndexes.has(index)))
      setSelected(new Set())
      notifications.show({ color: 'red', message: tr('failedToAddClassroomFile') })
      return
    }
    onClose()
  }

  const reasonKey = data && !data.available ? REASON_MESSAGE_KEYS[data.reason ?? ''] : undefined

  return (
    <Modal opened={opened} onClose={onClose} title={tr('classroomPickerTitle')} centered>
      <Stack spacing='md'>
        {loading && (
          <Group position='center' py='md'>
            <Loader size='sm' />
          </Group>
        )}

        {!loading && (failed || (data && !data.available)) && (
          <Alert color='yellow' variant='light' icon={<IconInfoCircle size={16} />}>
            {failed ? tr('classroomSyncSubmissionsFailed') : tr(reasonKey || 'projectClassroomNotLinked')}
          </Alert>
        )}

        {!loading && data?.available && attachments.length === 0 && (
          <Text size='sm' color='dimmed'>
            {tr('classroomPickerNoAttachments')}
          </Text>
        )}

        {!loading && data?.available && attachments.length > 0 && (
          <Stack spacing='xs'>
            <Group position='apart' align='center'>
              <Text size='sm' weight={600}>
                {tr('classroomPickerAttachmentsLabel')}
              </Text>
              <Anchor
                component='button'
                type='button'
                size='xs'
                onClick={() =>
                  setSelected(allSelected ? new Set() : new Set(attachments.map((_, index) => index)))
                }
              >
                {allSelected ? tr('classroomPickerDeselectAll') : tr('classroomPickerSelectAll')}
              </Anchor>
            </Group>
            {attachments.map((attachment, index) => (
              // eslint-disable-next-line react/no-array-index-key -- attachments have no id; list is reloaded per open
              <Group key={index} spacing='xs' noWrap align='center'>
                <Checkbox
                  checked={selected.has(index)}
                  onChange={() => toggle(index)}
                  label={
                    <Text size='sm' lineClamp={1}>
                      {attachment.name}
                    </Text>
                  }
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <Anchor
                  href={attachment.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={tr('openInGoogleClassroom')}
                >
                  <IconExternalLink size={14} />
                </Anchor>
              </Group>
            ))}
          </Stack>
        )}

        <Group position='right'>
          <Button variant='default' onClick={onClose} disabled={submitting}>
            {tr('cancel')}
          </Button>
          <Button onClick={handleAttach} loading={submitting} disabled={selected.size === 0}>
            {tr('classroomPickerAttachButton')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
